import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import DateRangeFilter from '@/components/DateRangeFilter';
import { fetchLeads, fetchParameters, createLead, exportLeadsUrl, exportLeadsCsvUrl } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Search, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import BrandWatermark from '@/components/BrandWatermark';

const initialForm = { customer_name: '', contact_person: '', email: '', mobile: '', industry: '', service_line: '', service_offered: '', proposal_value: 0, sales_owner: '', stage: 'Lead Identified', source: '', area: '', location: '', notes: '', priority: 'Medium' };

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [params, setParams] = useState({});
    const [searchParams] = useSearchParams();
    const [filters, setFilters] = useState(() => {
        const initial = {};
        ['status', 'win_loss', 'stage', 'sales_owner', 'service_line', 'priority', 'source'].forEach(k => {
            const v = searchParams.get(k);
            if (v) initial[k] = v;
        });
        return initial;
    });
    const [range, setRange] = useState({
        from: searchParams.get('date_from') || null,
        to: searchParams.get('date_to') || null,
    });
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const nav = useNavigate();

    const load = async () => {
        const query = { ...filters };
        if (q) query.q = q;
        if (range.from) query.date_from = range.from;
        if (range.to) query.date_to = range.to;
        const l = await fetchLeads(query);
        setLeads(l);
    };
    useEffect(() => { fetchParameters().then(setParams); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, range]);
    useEffect(() => {
        const t = setTimeout(() => { load(); }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const applySearch = (e) => { e.preventDefault(); load(); };

    const submitNew = async () => {
        try {
            const created = await createLead({ ...form, proposal_value: Number(form.proposal_value) });
            toast.success(`Created ${created.lead_id}`);
            setOpen(false);
            setForm(initialForm);
            load();
        } catch { toast.error('Create failed'); }
    };

    const setF = (k) => (v) => setFilters(f => ({ ...f, [k]: v || undefined }));

    return (
        <div>
            <PageHeader
                title="All Leads"
                subtitle={`${leads.length} records`}
                actions={
                    <div className="flex gap-2 flex-wrap">
                        <DateRangeFilter value={range} onChange={setRange} />
                        <a href={exportLeadsCsvUrl({ ...filters, q: q || undefined, date_from: range.from || undefined, date_to: range.to || undefined })} data-testid="btn-export-csv">
                            <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900 hover:bg-[#FFFFFF] gap-2">
                                <FileText className="w-4 h-4" /> CSV
                            </Button>
                        </a>
                        <a href={exportLeadsUrl()} data-testid="btn-export">
                            <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900 hover:bg-[#FFFFFF] gap-2">
                                <FileSpreadsheet className="w-4 h-4" /> Excel
                            </Button>
                        </a>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="btn-new-lead" className="bg-[#016a6d] hover:bg-[#014e50] gap-2">
                                    <Plus className="w-4 h-4" /> New Lead
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-2xl max-h-[85vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="font-display text-2xl">Create Lead</DialogTitle>
                                </DialogHeader>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Customer *" value={form.customer_name} onChange={v => setForm({ ...form, customer_name: v })} testId="field-customer" />
                                    <Field label="Contact Person" value={form.contact_person} onChange={v => setForm({ ...form, contact_person: v })} testId="field-contact" />
                                    <Field label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} testId="field-email" />
                                    <Field label="Mobile" value={form.mobile} onChange={v => setForm({ ...form, mobile: v })} testId="field-mobile" />
                                    <Field label="Industry" value={form.industry} onChange={v => setForm({ ...form, industry: v })} testId="field-industry" />
                                    <Field label="Area" value={form.area} onChange={v => setForm({ ...form, area: v })} testId="field-area" />
                                    <SelectField label="Service Line" value={form.service_line} onChange={v => setForm({ ...form, service_line: v })} options={params.service_lines || []} testId="field-service-line" />
                                    <Field label="Service Offered" value={form.service_offered} onChange={v => setForm({ ...form, service_offered: v })} testId="field-service-offered" />
                                    <Field label="Proposal Value (INR)" type="number" value={form.proposal_value} onChange={v => setForm({ ...form, proposal_value: v })} testId="field-value" />
                                    <SelectField label="Sales Owner" value={form.sales_owner} onChange={v => setForm({ ...form, sales_owner: v })} options={params.sales_owners || []} testId="field-owner" />
                                    <SelectField label="Stage" value={form.stage} onChange={v => setForm({ ...form, stage: v })} options={params.stages || []} testId="field-stage" />
                                    <SelectField label="Source" value={form.source} onChange={v => setForm({ ...form, source: v })} options={params.sources || []} testId="field-source" />
                                    <SelectField label="Priority" value={form.priority} onChange={v => setForm({ ...form, priority: v })} options={params.priorities || []} testId="field-priority" />
                                </div>
                                <DialogFooter>
                                    <Button onClick={submitNew} data-testid="btn-save-lead" className="bg-[#016a6d] hover:bg-[#014e50]">Create Lead</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />
            <div className="p-6 space-y-4">
                <div className="surface-1 rounded-md p-4 flex flex-wrap gap-3 items-center">
                    <form onSubmit={applySearch} className="flex items-center gap-2 flex-1 min-w-[240px]">
                        <Search className="w-4 h-4 text-slate-500" />
                        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customer, lead id, contact..." data-testid="search-input" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
                        <Button type="submit" size="sm" variant="outline" className="border-[#E2E8F0]" data-testid="btn-search">Search</Button>
                    </form>
                    <FilterSelect label="Stage" value={filters.stage} onChange={setF('stage')} options={params.stages} testId="filter-stage" />
                    <FilterSelect label="Owner" value={filters.sales_owner} onChange={setF('sales_owner')} options={params.sales_owners} testId="filter-owner" />
                    <FilterSelect label="Service" value={filters.service_line} onChange={setF('service_line')} options={params.service_lines} testId="filter-service" />
                    <FilterSelect label="Win/Loss" value={filters.win_loss} onChange={setF('win_loss')} options={params.win_loss} testId="filter-winloss" />
                    <FilterSelect label="Priority" value={filters.priority} onChange={setF('priority')} options={params.priorities} testId="filter-priority" />
                </div>

                <div className="surface-1 rounded-md overflow-hidden" data-testid="leads-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F1F5F9] border-b border-[#E2E8F0]">
                                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    <th className="px-4 py-3">Lead ID</th>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Service</th>
                                    <th className="px-4 py-3">Owner</th>
                                    <th className="px-4 py-3">Stage</th>
                                    <th className="px-4 py-3 text-right">Value</th>
                                    <th className="px-4 py-3 text-right">Prob</th>
                                    <th className="px-4 py-3">Priority</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map(l => (
                                    <tr
                                        key={l.id}
                                        onClick={() => nav(`/leads/${l.id}`)}
                                        data-testid={`lead-row-${l.lead_id}`}
                                        className="border-b border-[#E2E8F0] hover:bg-[#FFFFFF] cursor-pointer transition-colors"
                                    >
                                        <td className="px-4 py-3 text-slate-500 tabular-nums text-xs">{l.lead_id}</td>
                                        <td className="px-4 py-3 text-slate-900">{l.customer_name}<div className="text-[10px] text-slate-500">{l.contact_person}</div></td>
                                        <td className="px-4 py-3 text-slate-700 text-xs">{l.service_offered || l.service_line}</td>
                                        <td className="px-4 py-3 text-slate-700 text-xs">{l.sales_owner}</td>
                                        <td className="px-4 py-3"><StageBadge stage={l.stage} /></td>
                                        <td className="px-4 py-3 text-right text-slate-900 tabular-nums">{formatINR(l.proposal_value)}</td>
                                        <td className="px-4 py-3 text-right text-slate-500 tabular-nums text-xs">{(l.probability * 100).toFixed(0)}%</td>
                                        <td className="px-4 py-3"><PriorityBadge p={l.priority} /></td>
                                    </tr>
                                ))}
                                {leads.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-16 relative">
                                            <BrandWatermark size={220} opacity={0.15} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                            <div className="relative">
                                                <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                                                <div className="text-sm text-slate-500">No leads match filters</div>
                                                <div className="text-xs text-slate-400 mt-1">Try clearing search or filter fields</div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <Input value={value} type={type} onChange={e => onChange(e.target.value)} data-testid={testId} className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
        </div>
    );
}
function SelectField({ label, value, onChange, options = [], testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                <option value="">—</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
function FilterSelect({ label, value, onChange, options, testId }) {
    return (
        <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
            <option value="">{label}: All</option>
            {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}
export function StageBadge({ stage }) {
    const map = {
        'Lead Identified': 'bg-slate-100 text-slate-700 border-slate-300',
        'Pitching Services': 'bg-blue-950 text-blue-300 border-blue-900',
        'Qualifying Deal': 'bg-indigo-950 text-indigo-300 border-indigo-900',
        'Sending Proposal': 'bg-amber-950 text-amber-300 border-amber-900',
        'Negotiation': 'bg-orange-950 text-orange-300 border-orange-900',
        'Closure - Won': 'bg-emerald-950 text-emerald-300 border-emerald-900',
        'Closure - Lost': 'bg-red-950 text-red-300 border-red-900',
    };
    const cls = map[stage] || 'bg-slate-100 text-slate-700 border-slate-300';
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>{stage}</span>;
}
function PriorityBadge({ p }) {
    const color = p === 'High' ? 'text-[#FF453A]' : p === 'Medium' ? 'text-[#FF9F0A]' : 'text-slate-500';
    return <span className={`text-xs font-medium ${color}`}>{p || 'Low'}</span>;
}
